import React, { useState } from 'react';
import {
  PiInstagramLogo, PiTiktokLogo, PiYoutubeLogo, PiCheckCircle, PiTruck, PiShieldCheck, PiHeart, PiSparkle,
} from 'react-icons/pi';
import {
  PreviewFrame, Btn, Stars, PressBar, avatarBg, initialsFor, fillTokens,
} from './_shared';
import { addItemsToCart } from '../../../utils/shopifyStorefront';
import { pingLandingPageCta, lpLineProperty } from '../../../utils/landingAttribution';

// First variant with stock on hand, else the first variant at all — the
// payload has no inventory_policy, so a continue-selling variant can sit at
// qty ≤ 0 and still be purchasable; a truly dead pick surfaces as a
// /cart/add.js failure and shows the error toast.
function variantIdFor(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const inStock = variants.find((v) => Number(v?.inventoryQuantity) > 0);
  return (inStock || variants[0])?.id || null;
}

export default function CreatorSpotlightTemplate({ sections, entity, viewport, products = [], theme, editable, onSectionClick, page }) {
  const hero = sections?.hero || {};
  const about = sections?.about || {};
  const bundle = sections?.bundle || {};
  const reviews = sections?.reviews || {};
  const social = sections?.social || {};
  const finalCta = sections?.finalCta || {};
  const accent = theme?.primaryColor || '#8661FF';

  const featured = (bundle.productIds || []).map((id) => products.find((p) => p.id === id)).filter(Boolean);
  const display = featured.length ? featured : products.slice(0, 3);
  const firstName = entity?.name?.split(' ')[0] || 'me';

  // Bundle savings math. The percent only becomes real money via the
  // per-shop Function discount (it honors the _brella_discount property we
  // attach on add-to-cart), so on the live page the discounted prices are
  // shown ONLY when the backend confirmed that registration (discountReady)
  // — never advertise a saving checkout won't apply.
  const discountPct = Number(bundle.discountPercent) > 0 ? Number(bundle.discountPercent) : 0;
  const hasDiscount = discountPct > 0 && (editable || page?.discountReady === true);
  // Every LP-added line carries the page id (conversion attribution) and,
  // when live, the Function-discount spec.
  const lineProps = {
    ...lpLineProperty(page),
    ...(hasDiscount ? { _brella_discount: `percent:${discountPct}` } : {}),
  };
  const itemProps = Object.keys(lineProps).length ? lineProps : undefined;

  // `adding` holds what is busy: null | 'bundle' | a product id.
  const [adding, setAdding] = useState(null);
  const [cartError, setCartError] = useState(false);
  const bundleVariantIds = display.map(variantIdFor).filter(Boolean);

  const addAndGoToCart = async (busyKey, items) => {
    if (editable || adding || !items.length) return;
    // Cart-intent click → stats.ctaClicks, regardless of add outcome.
    pingLandingPageCta(page);
    setAdding(busyKey);
    setCartError(false);
    const r = await addItemsToCart({ items });
    setAdding(null);
    if (r.ok) {
      window.location.href = '/cart';
    } else {
      // A silent un-busy reads as a dead button — tell the buyer it failed.
      setCartError(true);
    }
  };

  const handleAddBundle = () => addAndGoToCart(
    'bundle',
    bundleVariantIds.map((variantId) => ({ variantId, quantity: 1, properties: itemProps })),
  );

  const handleAddOne = (p) => {
    const variantId = variantIdFor(p);
    if (!variantId) return;
    addAndGoToCart(p.id, [{ variantId, quantity: 1, properties: itemProps }]);
  };

  const scrollToBundle = () => {
    if (editable || typeof document === 'undefined') return;
    document.querySelector('[data-lp-section="bundle"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const bundleTotal = display.reduce((s, p) => s + (p?.price || 0), 0);
  const bundleSale = hasDiscount ? bundleTotal * (1 - discountPct / 100) : bundleTotal;
  const savings = bundleTotal - bundleSale;

  // Editable copy — `?? legacy` keeps old saved pages unchanged; explicit '' hides the element.
  const announcementText = hero.announcementText ?? (bundle.discountCallout ? `✨ ${bundle.discountCallout}` : '');
  const heroEyebrow = hero.eyebrow ?? `Hand-picked by ${firstName}`;
  const ratingText = hero.ratingText ?? '{starsAvg} · {reviewCount} shoppers';
  // Suppress rating text whose {reviewCount} token can't be filled — avoids a dangling "4.8 ·  shoppers".
  const showRatingText = ratingText && (reviews.reviewCount || !String(ratingText).includes('{reviewCount}'));
  const trustItems = Array.isArray(hero.trustItems) ? hero.trustItems : ['Free shipping $50+', '30-day returns'];
  const floatingCardLabel = hero.floatingCardLabel ?? 'Loved by';
  const floatingCardText = hero.floatingCardText ?? 'her community';
  const pressLogos = Array.isArray(hero.pressLogos) ? hero.pressLogos : [];
  const pressLabel = hero.pressLabel ?? 'AS FEATURED IN';
  const aboutEyebrow = about.eyebrow ?? 'MEET YOUR GUIDE';
  const bundleSubheadline = bundle.subheadline ?? 'The complete kit she uses every single day';
  const bestsellerBadge = bundle.bestsellerBadge ?? 'BESTSELLER';
  // Per-card add-to-cart label; merchant clears it to sell the bundle only.
  const cardCtaLabel = bundle.cardCtaLabel ?? 'Add to cart';
  const savingsText = bundle.savingsText ?? 'You save {savings} vs. buying separately';
  const ratingSummaryText = reviews.ratingSummaryText ?? '{starsAvg} avg from {reviewCount} verified reviews';
  const verifiedLabel = reviews.verifiedLabel ?? 'Verified buyer';
  const instagramUrl = social.instagramUrl || '';
  const tiktokUrl = social.tiktokUrl || '';
  const youtubeUrl = social.youtubeUrl || '';
  const finalCtaEyebrow = finalCta.eyebrow ?? 'LIMITED TIME';
  const finalCtaSubtext = finalCta.subtext ?? 'Your discount is already applied. Free shipping on orders over $50.';
  const finalCtaFootnote = finalCta.footnote ?? '🔒 Secure checkout · 30-day returns · 24/7 support';

  return (
    <PreviewFrame viewport={viewport} accent={accent} theme={theme} sections={sections} editable={editable} onSectionClick={onSectionClick}>
      {/* Add-to-cart failure toast — sold out between load and click, cart API hiccup, … */}
      {cartError && (
        <div style={{
          position: 'fixed', bottom: 84, left: '50%', transform: 'translateX(-50%)',
          background: '#B3261E', color: '#fff', borderRadius: 8,
          padding: '10px 18px', fontSize: 13, fontWeight: 600,
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)', zIndex: 60, maxWidth: '90vw',
        }}>
          Couldn’t add to cart — please try again.
        </div>
      )}
      {/* ANNOUNCEMENT BAR — hero.announcementText; empty hides the whole bar */}
      {announcementText ? (
        <div data-lp-section="hero">
          <div style={{
            background: '#1F1B33', color: '#fff', textAlign: 'center',
            padding: '8px 16px', fontSize: 12, fontWeight: 600, letterSpacing: 0.3,
          }}>
            <span data-lp-field="announcementText">{announcementText}</span>
          </div>
        </div>
      ) : null}

      {/* HERO — split layout, asymmetric, more credible */}
      <section data-lp-section="hero" style={{
        background: hero.backgroundColor || '#F8F4FF',
        padding: viewport === 'mobile' ? '40px 24px' : '72px 48px',
      }}>
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: viewport === 'mobile' ? '1fr' : '1.1fr 1fr',
          gap: 40, alignItems: 'center',
        }}>
          <div>
            {heroEyebrow ? (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: '#fff', padding: '6px 12px', borderRadius: 999,
                fontSize: 12, fontWeight: 600, color: accent, marginBottom: 20,
                border: `1px solid ${accent}33`,
              }}>
                <PiSparkle size={12} weight="fill" /> <span data-lp-field="eyebrow">{heroEyebrow}</span>
              </div>
            ) : null}
            <h1 style={{ fontSize: viewport === 'mobile' ? 32 : 48, fontWeight: 800, margin: '0 0 16px', color: '#1F1B33', lineHeight: 1.05, letterSpacing: -0.5 }}>
              {hero.headline || `Shop with ${firstName}`}
            </h1>
            <p style={{ fontSize: 17, color: '#4a4458', margin: '0 0 24px', lineHeight: 1.5 }}>
              {hero.subheadline || 'Get my favorite picks and an exclusive discount you won\'t find anywhere else.'}
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
              <Btn big onClick={scrollToBundle}>{hero.ctaLabel || 'Shop my picks →'}</Btn>
              {reviews.starsAvg ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4a4458' }}>
                  <Stars n={reviews.starsAvg} size={14} />
                  {showRatingText ? (
                    <span data-lp-field="ratingText">{fillTokens(ratingText, { starsAvg: reviews.starsAvg, reviewCount: reviews.reviewCount })}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
            {trustItems.length ? (
              <div data-lp-field="trustItems" style={{ display: 'flex', gap: 16, fontSize: 12, color: '#4a4458', flexWrap: 'wrap' }}>
                {trustItems.map((t, i) => {
                  const Icon = [PiTruck, PiShieldCheck][i] || PiCheckCircle;
                  return (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon size={14} /> {t}</span>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div style={{ position: 'relative' }}>
            {(() => {
              const heroImg = hero.heroImageUrl || entity?.avatarUrl || null;
              return (
                <div style={{
                  width: '100%', aspectRatio: '4/5', borderRadius: 16,
                  background: heroImg ? `url(${heroImg}) center/cover` : 'linear-gradient(135deg,#8661FF,#C4A8FF)',
                  boxShadow: '0 20px 50px rgba(134,97,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 80, fontWeight: 800,
                }}>
                  {!heroImg && (entity?.name?.[0] || '?')}
                </div>
              );
            })()}
            {(floatingCardLabel || floatingCardText) ? (
              <div style={{
                position: 'absolute', bottom: -16, left: -16,
                background: '#fff', padding: '12px 16px', borderRadius: 12,
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: accent, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PiHeart size={18} weight="fill" />
                </div>
                <div>
                  {floatingCardLabel ? <div data-lp-field="floatingCardLabel" style={{ fontSize: 11, color: '#7d7a86' }}>{floatingCardLabel}</div> : null}
                  {floatingCardText ? <div data-lp-field="floatingCardText" style={{ fontSize: 13, fontWeight: 700 }}>{floatingCardText}</div> : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* PRESS BAR — merchant-supplied logos only; PressBar renders nothing when the list is empty */}
      <div data-lp-section="hero">
        <PressBar logos={pressLogos} label={pressLabel} />
      </div>

      {/* ABOUT — testimonial-style intro */}
      <section data-lp-section="about" style={{ padding: viewport === 'mobile' ? '40px 24px' : '64px 48px', maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
        {aboutEyebrow ? (
          <div data-lp-field="eyebrow" style={{ fontSize: 13, color: accent, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>{aboutEyebrow}</div>
        ) : null}
        <h2 style={{ fontSize: 28, fontWeight: 700, margin: '0 0 16px', letterSpacing: -0.3 }}>
          {about.headline || `Hi, I'm ${firstName}`}
        </h2>
        <p style={{ fontSize: 17, lineHeight: 1.7, color: '#4a4458', margin: 0 }}>
          {about.body || 'I only recommend products I actually use. Every pick on this page has been tested in my own routine for at least 90 days.'}
        </p>
      </section>

      {/* BUNDLE — with savings math, "complete the look" energy */}
      <section data-lp-section="bundle" style={{ padding: viewport === 'mobile' ? '40px 16px' : '64px 32px', background: '#FAFAFC' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h2 style={{ fontSize: 30, fontWeight: 800, margin: '0 0 8px', letterSpacing: -0.5 }}>
              {bundle.headline || `${firstName}'s Essentials Bundle`}
            </h2>
            {bundleSubheadline ? (
              <p data-lp-field="subheadline" style={{ fontSize: 14, color: '#7d7a86', margin: 0 }}>
                {bundleSubheadline}
              </p>
            ) : null}
          </div>
          <div style={{
            display: 'grid', gap: 16,
            gridTemplateColumns: viewport === 'mobile' ? '1fr' : `repeat(${Math.min(3, Math.max(1, display.length || 3))}, 1fr)`,
            marginBottom: 24,
          }}>
            {(display.length ? display : [null, null, null]).map((p, i) => (
              <div key={p?.id || i} style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #ECE8F5', position: 'relative' }}>
                {i === 0 && bestsellerBadge ? (
                  <div data-lp-field="bestsellerBadge" style={{ position: 'absolute', top: 12, left: 12, background: accent, color: '#fff', fontSize: 10, fontWeight: 700, padding: '4px 8px', borderRadius: 4, letterSpacing: 0.5 }}>
                    {bestsellerBadge}
                  </div>
                ) : null}
                <div style={{ aspectRatio: '1/1', background: '#F2EEFF', borderRadius: 8, marginBottom: 12,
                  backgroundImage: p?.imageUrl ? `url(${p.imageUrl})` : undefined,
                  backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{p?.title || 'Product name'}</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: accent }}>${(hasDiscount ? (p?.price ?? 0) * (1 - discountPct / 100) : (p?.price ?? 0)).toFixed(2)}</span>
                  {hasDiscount && (
                    <span style={{ fontSize: 12, color: '#9892a3', textDecoration: 'line-through' }}>${(p?.price ?? 0).toFixed(2)}</span>
                  )}
                </div>
                {p && cardCtaLabel ? (
                  <button
                    type="button"
                    data-lp-field="cardCtaLabel"
                    onClick={() => handleAddOne(p)}
                    disabled={!!adding || !variantIdFor(p)}
                    style={{
                      marginTop: 10, width: '100%', padding: '9px 12px',
                      borderRadius: 'var(--lp-radius, 8px)',
                      border: `1px solid ${accent}55`, background: '#fff', color: accent,
                      fontSize: 13, fontWeight: 700,
                      cursor: (!!adding || !variantIdFor(p)) ? 'not-allowed' : 'pointer',
                      opacity: (!!adding || !variantIdFor(p)) ? 0.65 : 1,
                    }}
                  >
                    {adding === p.id ? 'Adding…' : cardCtaLabel}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
          {/* Bundle CTA card */}
          <div style={{
            background: '#1F1B33', color: '#fff', borderRadius: 12,
            padding: viewport === 'mobile' ? 20 : 28,
            display: 'grid', gridTemplateColumns: viewport === 'mobile' ? '1fr' : '1fr auto',
            alignItems: 'center', gap: 20,
          }}>
            <div>
              <div data-lp-field="ctaCardEyebrow" style={{ fontSize: 12, color: accent, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
                {bundle.ctaCardEyebrow ? bundle.ctaCardEyebrow : (hasDiscount ? 'BUNDLE & SAVE' : 'SHOP THE BUNDLE')}
              </div>
              <div data-lp-field="ctaCardText" style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
                {fillTokens(bundle.ctaCardText || 'Get all {count} for {price}', { count: display.length || 3, price: `$${bundleSale.toFixed(2)}` })}
              </div>
              {hasDiscount && savingsText ? (
                <div data-lp-field="savingsText" style={{ fontSize: 13, opacity: 0.8 }}>
                  {fillTokens(savingsText, { savings: `$${savings.toFixed(2)}` })}
                </div>
              ) : null}
            </div>
            <Btn big color={accent} onClick={handleAddBundle} disabled={!!adding || !bundleVariantIds.length}>
              {adding === 'bundle' ? 'Adding…' : (bundle.ctaLabel || 'Add bundle to cart →')}
            </Btn>
          </div>
        </div>
      </section>

      {/* REVIEWS — UGC-style with avatars. Only render when the merchant has real reviews; never fabricate names/ratings/counts. */}
      {reviews.items?.length ? (
        <section data-lp-section="reviews" style={{ padding: viewport === 'mobile' ? '40px 24px' : '64px 32px', maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            {reviews.starsAvg ? <Stars n={reviews.starsAvg} size={20} /> : null}
            <h2 style={{ fontSize: 28, fontWeight: 700, margin: '12px 0 4px', letterSpacing: -0.3 }}>
              {reviews.headline || 'Loved by her community'}
            </h2>
            {reviews.starsAvg && reviews.reviewCount && ratingSummaryText ? (
              <p data-lp-field="ratingSummaryText" style={{ fontSize: 13, color: '#7d7a86', margin: 0 }}>
                {fillTokens(ratingSummaryText, { starsAvg: reviews.starsAvg, reviewCount: reviews.reviewCount })}
              </p>
            ) : null}
          </div>
          <div style={{ display: 'grid', gap: 16, gridTemplateColumns: viewport === 'mobile' ? '1fr' : 'repeat(3, 1fr)' }}>
            {reviews.items.map((r, i) => (
              <div key={i} style={{ background: '#FAFAFC', borderRadius: 12, padding: 20, border: '1px solid #ECE8F5' }}>
                {(r.stars || r.rating) ? <Stars n={r.stars || r.rating} /> : null}
                <p style={{ fontSize: 14, lineHeight: 1.6, color: '#1a1a1a', margin: '10px 0 16px', fontWeight: 500 }}>"{r.quote || r.text}"</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarBg(r.name || r.initials || `R${i}`), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#1F1B33' }}>
                    {initialsFor(r, i)}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                    {(r.verified && verifiedLabel) ? (
                      <div style={{ fontSize: 11, color: '#7d7a86', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <PiCheckCircle size={12} weight="fill" style={{ color: '#22B364' }} /> <span data-lp-field="verifiedLabel">{verifiedLabel}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* SOCIAL */}
      <section data-lp-section="social" style={{ padding: '40px 24px', textAlign: 'center', background: '#F8F4FF' }}>
        <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 16px' }}>{social.headline || `Follow ${firstName}`}</h3>
        <div style={{ display: 'inline-flex', gap: 14 }}>
          {social.showInstagram && (
            <a {...(instagramUrl ? { href: instagramUrl, target: '_blank', rel: 'noopener' } : {})} style={{ width: 44, height: 44, borderRadius: '50%', background: '#fff', color: accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}><PiInstagramLogo size={22} /></a>
          )}
          {social.showTiktok && (
            <a {...(tiktokUrl ? { href: tiktokUrl, target: '_blank', rel: 'noopener' } : {})} style={{ width: 44, height: 44, borderRadius: '50%', background: '#fff', color: accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}><PiTiktokLogo size={22} /></a>
          )}
          {social.showYoutube && (
            <a {...(youtubeUrl ? { href: youtubeUrl, target: '_blank', rel: 'noopener' } : {})} style={{ width: 44, height: 44, borderRadius: '50%', background: '#fff', color: accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}><PiYoutubeLogo size={22} /></a>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-lp-section="finalCta" style={{ padding: viewport === 'mobile' ? '56px 24px' : '80px 32px', textAlign: 'center', background: '#1F1B33', color: '#fff' }}>
        {finalCtaEyebrow ? (
          <div data-lp-field="eyebrow" style={{ fontSize: 12, color: accent, fontWeight: 700, letterSpacing: 1.5, marginBottom: 12 }}>{finalCtaEyebrow}</div>
        ) : null}
        <h2 style={{ fontSize: viewport === 'mobile' ? 30 : 38, fontWeight: 800, margin: '0 0 12px', letterSpacing: -0.5 }}>
          {finalCta.headline || 'Ready to shop?'}
        </h2>
        {finalCtaSubtext ? (
          <p data-lp-field="subtext" style={{ fontSize: 15, opacity: 0.85, margin: '0 0 28px', maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
            {finalCtaSubtext}
          </p>
        ) : null}
        <Btn big color={accent} textColor="#1F1B33" onClick={scrollToBundle}>{finalCta.ctaLabel || `Shop ${firstName}'s picks →`}</Btn>
        {finalCtaFootnote ? (
          <div data-lp-field="footnote" style={{ marginTop: 20, fontSize: 12, opacity: 0.6 }}>
            {finalCtaFootnote}
          </div>
        ) : null}
      </section>
    </PreviewFrame>
  );
}
